import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { Box } from '@abyss/web/ui/Box';
import { Brandmark } from '@abyss/web/ui/Brandmark';
import { DropdownMenu } from '@abyss/web/ui/DropdownMenu';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { MouseEvent as ReactMouseEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  getActualLanguageCode,
  setLanguage,
} from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import {
  LANGUAGES,
  Locale,
} from '../../frontends/ProviderSearch/context/Internationalization/types';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { Constants } from '../Constants';
import { FontSizeModal } from '../GuestHeader/FontSizeModal';

interface MenuItem {
  label: string;
  value: Locale;
  checked: boolean;
  onChange: () => void;
}

export const ProviderDirectoryHeader = () => {
  const { t, i18n } = useTranslation();
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();

  const supportedLanguages = [Locale.enUS, Locale.esUS];
  // Support both 'locale' and 'lang' params for backward compatibility
  const localeFromParam =
    searchParams.get('locale') || searchParams.get('lang')
      ? ((searchParams.get('locale') || searchParams.get('lang')) as Locale)
      : Locale.enUS;

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { locale, setLocale } = chipStore;

  useEffect(() => {
    if (localeFromParam) {
      handleOnLanguageChange(localeFromParam);
    }
  }, [localeFromParam]);

  const handleOnLanguageChange = async (locale: Locale) => {
    const chosenLanguage =
      LANGUAGES[getActualLanguageCode(locale, supportedLanguages)];
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES,
      JSON.stringify(supportedLanguages)
    );
    storage.cookie.set(Constants.PSLOCALE, chosenLanguage.locale);
    setLocale(chosenLanguage.locale);
    await i18n.changeLanguage(chosenLanguage.locale);
    await setLanguage(chosenLanguage);

    const url = new URL(window.location.href);
    const currentLocale =
      url.searchParams.get('locale') || url.searchParams.get('lang');
    if (currentLocale && currentLocale !== chosenLanguage.locale) {
      url.searchParams.set('locale', chosenLanguage.locale);
      url.searchParams.delete('lang');
      window.location.href = url.toString();
    }
  };

  const languageMenuItems = [
    {
      checkboxes: Object?.values(LANGUAGES)?.reduce<MenuItem[]>((acc, item) => {
        if (supportedLanguages?.includes(item?.locale)) {
          acc.push({
            label: item.name,
            value: item.locale,
            checked: locale === item.locale,
            onChange: () => {
              handleOnLanguageChange(item?.locale);
            },
          });
        }
        return acc;
      }, []),
    },
  ];

  const getHyperLink = (
    e: ReactMouseEvent<HTMLAnchorElement>,
    locale: string
  ) => {
    e.preventDefault();
    switch (locale) {
      case Locale.enUS:
        return window.open(
          'https://www.uhc.com/contact-us?locale=en-US',
          '_blank'
        );
      case Locale.esUS:
        return window.open('https://es.uhc.com/contact-us', '_blank');
      default:
        return window.open(
          'https://www.uhc.com/contact-us?locale=en-US',
          '_blank'
        );
    }
  };

  return (
    <Box
      css={{
        'abyss-box-root': {
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '$white',
          width: '100%',
          height: 'auto',
          opacity: 1,
          fontFamily: '$primary',
          marginTop: '0px',
          padding: '0px',
        },
      }}
    >
      <Flex
        css={{
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '16px',
          height: '40px',
          backgroundColor: '#F3F3F3',
          padding: '0 48px',
        }}
      >
        <DropdownMenu
          after={<IconSymbol icon="keyboard_arrow_down" />}
          css={{
            fontSize: '$sm',
          }}
          data-testid="language-dropdown"
          label="Language"
          menuItems={languageMenuItems}
          outline={false}
          value={locale}
          width={110}
        />
        <FontSizeModal />
        <Link
          css={{
            fontSize: '$sm',
            textDecoration: 'none',
            '& .abyss-link-icon': { display: 'none' },
          }}
          data-testid="help-link"
          href={'#'}
          onClick={(e) => getHyperLink(e, locale)}
          openNewWindow={true}
        >
          {t('HELP')}
        </Link>
      </Flex>
      <Flex
        css={{
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '56px',
          margin: '11px 0',
          padding: '0 48px',
        }}
      >
        <Box
          css={{
            'abyss-box-root': {
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              maxWidth: '242px',
              height: '48px',
              backgroundColor: '$white',
            },
          }}
        >
          <Brandmark
            affiliate="uhc"
            aria-label={t('GUEST_LOGO_ACCESSIBILITY')} // change this
            brand="uhc"
            color="blue"
            css={{
              'abyss-brandmark': {
                height: '48px',
                width: 'auto',
                display: 'block',
              },
            }}
            data-testid="providerDirectory-logo-brandmark"
            role="button"
            size="100%"
            tabIndex={0}
            variant="lockup"
          />
        </Box>
      </Flex>
      <Box
        css={{
          alignItems: 'center',
          height: '36px',
          lineHeight: '36px',
          backgroundColor: '#002677',
          color: '#ffffff',
          padding: '0 48px',
        }}
      ></Box>
    </Box>
  );
};

export default ProviderDirectoryHeader;
